import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { api, type Me } from '../api';
import { money } from '../form';
import { errorText, useI18n } from '../i18n';
import { Alert, PageHead, PanelShell } from '../layout';

interface Work { bank: number; queue: number; inbox: number; overdue: number; overdueSum: number; lastBank: string | null; missed: { title: string; period: string; due: string }[]; next: { title: string; period: string; due: string } | null }
interface Client { id: number; clientId: number; status: 'requested' | 'active'; name: string; code: string | null; kind: 'company' | 'iv'; assignedUserId: number | null; assignedName: string | null; note: string; since: string; work: Work | null;
  /** The client's own owner, or the open invitation to one. */
  owner?: { name: string; email: string } | null; ownerInvited?: string | null }
interface Board { clients: Client[]; people: { id: number; name: string }[]; me: number }

/** How much needs the firm: missed taxes first, then unsorted payments and documents. */
const weight = (w: Work | null) => (w ? w.missed.length * 1000 + w.queue * 10 + w.bank + w.inbox + w.overdue : -1);

/** Opens a client's panel (the session moves there; the bar on top leads back). */
export function useOpenCompany() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return async (companyId: number, to = '/') => {
    await api.post('api/v1/auth/switch', { companyId });
    queryClient.clear();
    await navigate({ to });
  };
}

/**
 * Buhalterio panelė: every client of the accounting firm on one board — what waits in each (bank payments without
 * a reason, documents for the accountant, invoices by e-mail, late invoices, missed and next taxes) — who looks
 * after it, and one click into its panel. Requests from new clients and opening a new client.
 */
export function FirmPage({ me }: { me: Me }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const open = useOpenCompany();
  const [mine, setMine] = useState(false);
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);
  const [inviting, setInviting] = useState<Client | null>(null);
  const [view, setView] = useState<'clients' | 'work'>(() => (window.location.hash === '#work' ? 'work' : 'clients'));
  const data = useQuery({ queryKey: ['firm-clients'], queryFn: () => api.get<Board>('api/v1/firm/clients') });
  const act = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: async () => { setAdding(false); await queryClient.invalidateQueries({ queryKey: ['firm-clients'] }); await queryClient.invalidateQueries({ queryKey: ['me'] }); },
  });
  const owner = me.company.role === 'owner' && !me.viewAsAdmin;
  const d = data.data;
  const requests = d?.clients.filter((c) => c.status === 'requested') ?? [];
  const needle = q.trim().toLowerCase();
  const active = (d?.clients.filter((c) => c.status === 'active') ?? [])
    .filter((c) => (!mine || c.assignedUserId === d!.me) && (!needle || c.name.toLowerCase().includes(needle) || (c.code ?? '').includes(needle)))
    .sort((a, b) => weight(b.work) - weight(a.work) || a.name.localeCompare(b.name));
  const total = (key: 'bank' | 'queue' | 'missed') => active.reduce((s, c) => s + (key === 'missed' ? c.work?.missed.length ?? 0 : c.work?.[key] ?? 0), 0);
  const addClient = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const v = new FormData(e.currentTarget);
    act.mutate(() => api.post('api/v1/firm/clients', { name: String(v.get('name') ?? ''), code: String(v.get('code') ?? '') || undefined, kind: String(v.get('kind') ?? 'company'), isVatPayer: v.get('vat') === '1' }));
  };

  return (
    <PanelShell me={me} head={<PageHead eyebrow={me.company.name} title={t('Buhalterio panelė')} text={t('Visi klientai vienoje vietoje: kas laukia, kas vėluoja, kas atsakingas.')}
      action={<button type="button" className="button button--primary" onClick={() => setAdding(!adding)}>+ {t('Naujas klientas')}</button>} />}>
      {data.isError ? <Alert kind="error">{errorText(t, data.error)}</Alert> : null}
      {act.isError ? <Alert kind="error">{errorText(t, act.error)}</Alert> : null}

      {adding ? (
        <section className="panel-card">
          <h2>{t('Naujas klientas')}</h2>
          <p className="muted">{t('Įmonė sukuriama iš karto su Premium; jūs būsite jos savininkas per buhalterinę įmonę, kol pakviesite kliento žmones.')}</p>
          <form className="form-grid form-grid--4" onSubmit={addClient}>
            <label className="span-2">{t('Pavadinimas')}<input name="name" required maxLength={200} /></label>
            <label>{t('Įmonės kodas')}<input name="code" maxLength={30} /></label>
            <label>{t('Veiklos forma')}<select name="kind"><option value="company">{t('Įmonė (MB, UAB…)')}</option><option value="iv">{t('Individuali veikla')}</option></select></label>
            <label className="check"><input type="checkbox" name="vat" value="1" />{t('PVM mokėtojas')}</label>
            <div className="form-actions"><button className="button button--primary" disabled={act.isPending}>{t('Sukurti')}</button></div>
          </form>
        </section>
      ) : null}

      {inviting ? (
        <section className="panel-card">
          <h2>{t('Pakviesti savininką: {name}', { name: inviting.name })}</h2>
          <p className="muted">{t('Kliento savininkas gaus kvietimą el. paštu. Priėmus įmonė bus jo, o jūsų įmonės žmonės liks joje nariais, kol tvarkote apskaitą.')}</p>
          <form className="firm-invite-form" onSubmit={(e) => { e.preventDefault(); const email = String(new FormData(e.currentTarget).get('email') ?? '').trim(); act.mutate(() => api.post(`api/v1/firm/clients/${inviting.id}/invite-owner`, { email }).then(() => setInviting(null))); }}>
            <input type="email" name="email" required placeholder={t('El. paštas')} autoFocus />
            <button className="button button--primary button--small" disabled={act.isPending}>{t('Siųsti kvietimą')}</button>
            <button type="button" className="button button--ghost button--small" onClick={() => setInviting(null)}>{t('Atšaukti')}</button>
          </form>
        </section>
      ) : null}

      {requests.length ? (
        <section className="panel-card">
          <p className="eyebrow">{t('Nauji prašymai')}</p><h2>{t('Įmonės, norinčios, kad tvarkytumėte jų apskaitą')}</h2>
          <ul className="team-list">
            {requests.map((c) => (
              <li key={c.id}>
                <div className="customer-avatar">{c.name.slice(0, 2).toUpperCase()}</div>
                <div className="team-who"><strong>{c.name}</strong><small>{c.code ? `${t('kodas')} ${c.code} · ` : ''}{c.since.slice(0, 10)}</small></div>
                {owner ? <>
                  <button type="button" className="button button--primary button--small" disabled={act.isPending} onClick={() => act.mutate(() => api.post(`api/v1/firm/clients/${c.id}/accept`))}>{t('Priimti')}</button>
                  <button type="button" className="button button--ghost button--small" disabled={act.isPending} onClick={() => { if (confirm(t('Atmesti {name} prašymą?', { name: c.name }))) act.mutate(() => api.post(`api/v1/firm/clients/${c.id}/end`)); }}>{t('Atmesti')}</button>
                </> : <span className="badge">{t('Laukia savininko')}</span>}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <nav className="project-tabs page-tabs">
        <button type="button" className={view === 'clients' ? 'active' : ''} onClick={() => { setView('clients'); history.replaceState(null, '', '#clients'); }}>{t('Kas laukia')}</button>
        <button type="button" className={view === 'work' ? 'active' : ''} onClick={() => { setView('work'); history.replaceState(null, '', '#work'); }}>{t('Mėnesio darbai')}</button>
      </nav>

      {view === 'work' ? <WorkBoard me={me} /> : <>
      <section className="stat-grid firm-stats">
        <article><span>{t('Klientų')}</span><strong>{active.length}</strong><small>{mine ? t('mano') : t('visi')}</small></article>
        <article className={total('missed') ? 'stat--alert' : undefined}><span>{t('Praleisti terminai')}</span><strong>{total('missed')}</strong><small>{t('mokesčiai ir deklaracijos')}</small></article>
        <article><span>{t('Banko mokėjimai be priežasties')}</span><strong>{total('bank')}</strong><small>{t('visų klientų')}</small></article>
        <article><span>{t('Buhalteriui')}</span><strong>{total('queue')}</strong><small>{t('laukia sutvarkymo')}</small></article>
      </section>

      <section className="panel-card">
        <div className="card-head">
          <div><p className="eyebrow">{t('Klientai')}</p><h2>{t('Kas laukia')}</h2></div>
          <div className="form-actions">
            <input type="search" placeholder={t('Paieška')} value={q} onChange={(e) => setQ(e.target.value)} aria-label={t('Paieška')} />
            <div className="calendar-view-switch" role="group">
              <a href="#all" className={mine ? undefined : 'active'} aria-pressed={!mine} onClick={(e) => { e.preventDefault(); setMine(false); }}>{t('Visi')}</a>
              <a href="#mine" className={mine ? 'active' : undefined} aria-pressed={mine} onClick={(e) => { e.preventDefault(); setMine(true); }}>{t('Mano')}</a>
            </div>
          </div>
        </div>
        <div className="table-wrap"><table className="invoice-table firm-table">
          <thead><tr><th>{t('Klientas')}</th><th>{t('Atsakingas')}</th><th className="right">{t('Bankas')}</th><th className="right">{t('Buhalteriui')}</th><th className="right">{t('El. paštu')}</th><th className="right">{t('Vėluoja')}</th><th>{t('Mokesčiai')}</th><th>{t('Paskutinis išrašas')}</th><th /></tr></thead>
          <tbody>
            {d && !active.length ? <tr><td colSpan={9} className="empty">{d.clients.length ? t('Pagal filtrą klientų nėra.') : t('Klientų dar nėra. Klientas jus pakviečia savo nustatymuose (jūsų įmonės kodu), arba sukurkite jį patys.')}</td></tr> : null}
            {active.map((c) => {
              const w = c.work;
              return (
                <tr key={c.id}>
                  <td><strong>{c.name}</strong><small>{c.kind === 'iv' ? t('Individuali veikla') : c.code ? `${t('kodas')} ${c.code}` : ''}{c.note ? ` · ${c.note}` : ''}</small>
                    {c.owner ? <small>{t('Savininkas')}: {c.owner.name}</small>
                      : c.ownerInvited ? <small className="muted">{t('Pakviestas savininkas: {email}', { email: c.ownerInvited })}</small>
                      : <button type="button" className="text-link firm-invite" onClick={() => setInviting(c)}>{t('Pakviesti savininką')}</button>}</td>
                  <td><select aria-label={t('Atsakingas')} value={c.assignedUserId ?? ''} disabled={act.isPending} onChange={(e) => act.mutate(() => api.put(`api/v1/firm/clients/${c.id}`, { assignedUserId: e.target.value ? Number(e.target.value) : null }))}>
                    <option value="">—</option>{d!.people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select></td>
                  <td className={`right${w?.bank ? ' firm-hot' : ''}`}>{w?.bank ?? ''}</td>
                  <td className={`right${w?.queue ? ' firm-hot' : ''}`}>{w?.queue ?? ''}</td>
                  <td className="right">{w?.inbox || ''}</td>
                  <td className="right">{w?.overdue ? <>{w.overdue}<small>{money(w.overdueSum)}</small></> : ''}</td>
                  <td>{w?.missed.length ? <span className="badge badge--overdue" title={w.missed.map((m) => `${m.title} ${m.period}`).join('\n')}>{t('Praleista: {n}', { n: w.missed.length })}</span> : null}
                    {w?.next ? <small>{w.next.due} · {w.next.title}</small> : null}</td>
                  <td>{w?.lastBank ?? '—'}</td>
                  <td className="right"><button type="button" className="button button--ghost button--small" onClick={() => void open(c.clientId)}>{t('Atidaryti')} →</button></td>
                </tr>
              );
            })}
          </tbody>
        </table></div>
        <p className="muted account-note">{t('Klientas jus prideda: Įmonė → Vartotojai → „Buhalterinė įmonė“, įvedęs jūsų įmonės kodą.')}</p>
      </section>
      </>}
    </PanelShell>
  );
}

interface Link { id: number; status: 'requested' | 'active'; firm: string; code: string | null; since: string }

/** The client's side: which accounting firm keeps its books (asked by the firm's company code), and ending it. */
export function FirmLinkCard({ me }: { me: Me }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const data = useQuery({ queryKey: ['firm-link'], queryFn: () => api.get<{ link: Link | null }>('api/v1/firm-link') });
  const act = useMutation({ mutationFn: (fn: () => Promise<unknown>) => fn(), onSuccess: () => Promise.all(['firm-link', 'team'].map((key) => queryClient.invalidateQueries({ queryKey: [key] }))) });
  const owner = me.company.role === 'owner' && !me.viewAsAdmin && !me.firm;
  const link = data.data?.link;
  return (
    <section className="panel-card">
      <p className="eyebrow">{t('Buhalterinė įmonė')}</p>
      <h2>{link ? link.firm : t('Jūsų buhalterinė įmonė')}</h2>
      {act.isError ? <Alert kind="error">{errorText(t, act.error)}</Alert> : null}
      {link?.status === 'active' ? <p className="muted">{t('Tvarko jūsų apskaitą nuo {date}. Jos žmonės mato ir tvarko jūsų įmonės duomenis.', { date: link.since.slice(0, 10) })}</p> : null}
      {link?.status === 'requested' ? <p className="muted">{t('Prašymas išsiųstas — laukiama, kol įmonė patvirtins.')}</p> : null}
      {!link ? <p className="muted">{t('Jei jūsų apskaitą tvarko buhalterinė įmonė, dirbanti su FakturaSmart Pro, įveskite jos įmonės kodą: jos žmonės matys jūsų duomenis ir galės juos tvarkyti.')}</p> : null}
      {owner && !link ? (
        <form className="inline-form" onSubmit={(e) => { e.preventDefault(); const code = String(new FormData(e.currentTarget).get('code') ?? '').trim(); if (code) act.mutate(() => api.post('api/v1/firm-link', { firmCode: code })); }}>
          <input name="code" placeholder={t('Įmonės kodas')} required minLength={5} maxLength={30} />
          <button className="button button--primary button--small" disabled={act.isPending}>{t('Paprašyti')}</button>
        </form>
      ) : null}
      {owner && link ? <button type="button" className="button button--ghost button--small" disabled={act.isPending}
        onClick={() => { if (confirm(link.status === 'active' ? t('Nutraukti ryšį su {firm}? Jos žmonės nebematys jūsų įmonės.', { firm: link.firm }) : t('Atšaukti prašymą?'))) act.mutate(() => api.delete('api/v1/firm-link')); }}>
        {link.status === 'active' ? t('Nutraukti ryšį') : t('Atšaukti prašymą')}</button> : null}
    </section>
  );
}

interface WorkRow {
  id: number; clientId: number; name: string; kind: string; assignedUserId: number | null; ready: boolean;
  closed: { closedAt: string; closedBy: string | null; note: string } | null;
  steps: { bank: boolean | null; unlinked: number; drafts: number; missingFiles: number; inbox: number; forAccountant: number; declarations: { filed: number; total: number; open: string[] } };
}

const lastMonth = () => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 7); };

/**
 * Mėnesio darbai: for a month (the last one by default), every client's month-end steps side by side — the bank
 * statement to the month's end, payments without a reason, drafts, received invoices without their document, the
 * inbox, what waits for the accountant, the declarations — and closing the month from here.
 */
function WorkBoard({ me }: { me: Me }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const open = useOpenCompany();
  const [month, setMonth] = useState(lastMonth);
  const [mine, setMine] = useState(false);
  const data = useQuery({ queryKey: ['firm-work', month], queryFn: () => api.get<{ month: string; clients: WorkRow[]; me: number }>(`api/v1/firm/work?month=${month}`) });
  const act = useMutation({ mutationFn: (fn: () => Promise<unknown>) => fn(), onSuccess: () => queryClient.invalidateQueries({ queryKey: ['firm-work'] }) });
  const rows = (data.data?.clients ?? []).filter((c) => !mine || c.assignedUserId === data.data!.me)
    .sort((a, b) => Number(Boolean(a.closed)) - Number(Boolean(b.closed)) || Number(b.ready) - Number(a.ready) || a.name.localeCompare(b.name));
  const closed = rows.filter((c) => c.closed).length;
  const cell = (n: number) => (n ? <td className="right firm-hot">{n}</td> : <td className="right firm-ok">✓</td>);
  return (
    <>
      {data.isError ? <Alert kind="error">{errorText(t, data.error)}</Alert> : null}
      {act.isError ? <Alert kind="error">{errorText(t, act.error)}</Alert> : null}
      <section className="panel-card">
        <div className="card-head">
          <div><p className="eyebrow">{t('Mėnesio darbai')}</p><h2>{t('Uždaryta {closed} iš {all}', { closed, all: rows.length })}</h2></div>
          <div className="form-actions">
            <input type="month" value={month} max={new Date().toISOString().slice(0, 7)} onChange={(e) => e.target.value && setMonth(e.target.value)} aria-label={t('Mėnuo')} />
            <div className="calendar-view-switch" role="group">
              <a href="#work" className={mine ? undefined : 'active'} aria-pressed={!mine} onClick={(e) => { e.preventDefault(); setMine(false); }}>{t('Visi')}</a>
              <a href="#work" className={mine ? 'active' : undefined} aria-pressed={mine} onClick={(e) => { e.preventDefault(); setMine(true); }}>{t('Mano')}</a>
            </div>
          </div>
        </div>
        <div className="table-wrap"><table className="invoice-table firm-table firm-work">
          <thead><tr><th>{t('Klientas')}</th><th>{t('Išrašas')}</th><th className="right">{t('Mokėjimai')}</th><th className="right">{t('Juodraščiai')}</th><th className="right">{t('Be dokumento')}</th>
            <th className="right">{t('El. paštu')}</th><th className="right">{t('Buhalteriui')}</th><th>{t('Deklaracijos')}</th><th>{t('Mėnuo')}</th><th /></tr></thead>
          <tbody>
            {data.data && !rows.length ? <tr><td colSpan={10} className="empty">{t('Klientų dar nėra.')}</td></tr> : null}
            {rows.map((c) => {
              const s = c.steps;
              return (
                <tr key={c.id} className={c.closed ? 'is-neutral' : undefined}>
                  <td><strong>{c.name}</strong>{c.ready && !c.closed ? <small className="firm-ok">{t('Viskas sutvarkyta — galima uždaryti')}</small> : null}</td>
                  <td>{s.bank === null ? <span className="muted">—</span> : s.bank ? <span className="firm-ok">✓</span> : <span className="firm-hot">{t('Trūksta')}</span>}</td>
                  {cell(s.unlinked)}{cell(s.drafts)}{cell(s.missingFiles)}{cell(s.inbox)}{cell(s.forAccountant)}
                  <td>{s.declarations.total ? <span className={s.declarations.filed === s.declarations.total ? 'firm-ok' : 'firm-hot'} title={s.declarations.open.join('\n')}>{s.declarations.filed}/{s.declarations.total}</span> : <span className="muted">—</span>}</td>
                  <td>{c.closed ? <span className="badge badge--paid" title={c.closed.note}>{t('Uždaryta')}{c.closed.closedBy ? ` · ${c.closed.closedBy}` : ''}</span>
                    : <button type="button" className="button button--small button--ghost" disabled={act.isPending}
                      onClick={() => { if (c.ready || confirm(t('{name}: dar ne viskas sutvarkyta. Vis tiek uždaryti mėnesį?', { name: c.name }))) act.mutate(() => api.post(`api/v1/firm/clients/${c.id}/close`, { month })); }}>{t('Uždaryti')}</button>}</td>
                  <td className="right"><button type="button" className="button button--ghost button--small" onClick={() => void open(c.clientId, '/close')}>{t('Atidaryti')} →</button></td>
                </tr>
              );
            })}
          </tbody>
        </table></div>
        <p className="muted account-note">{t('Tie patys žingsniai, kuriuos klientas mato puslapyje „Uždarymas“. Mėnesį uždaryti galima ir iš čia; tai nieko neužrakina.')}</p>
      </section>
    </>
  );
}
